import { GalleryItemType } from 'components/Basic/Gallery/Gallery';
import Image from 'components/Basic/Image/Image';
import { ProductDetailThumbnails } from 'components/Pages/ProductDetail/ProductDetailThumbnails';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { SimpleFlagType } from 'types/flag';
import { ImageType } from 'types/image';

const Gallery = dynamic(() => import('components/Basic/Gallery/Gallery').then((component) => component.Gallery));

type Props = {
    flags: SimpleFlagType[];
    images: ImageType[];
    productName: string;
    youtubeVideoId: string | null;
};

export const ProductDetailGallery: FC<Props> = ({ images, youtubeVideoId, productName }) => {
    const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | undefined>();
    const { t } = useTranslation();

    if (images.length === 0) {
        return null;
    }

    const mainImage = images[0].url;

    const galleryImages = (() => {
        const items: GalleryItemType[] = images.map((image) => ({ src: image.url }));

        if (youtubeVideoId) {
            items.unshift({
                src: `https://www.youtube.com/embed/${youtubeVideoId}`,
                isVideo: true,
            });
        }

        return items;
    })();

    return (
        <div className="w-full lg:flex lg:items-start">
            <button
                className="relative mx-auto my-0 block h-[335px] w-full max-w-[600px] lg:order-1 lg:h-[600px] lg:self-stretch"
                tabIndex={0}
                onClick={() => setActiveGalleryIndex(youtubeVideoId ? 1 : 0)}
            >
                <Image
                    fill
                    priority
                    alt={t('Image of ') + productName}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    src={mainImage}
                />
            </button>
            <ProductDetailThumbnails
                images={images.slice(1, images.length)}
                productName={productName}
                youtubeVideo={{
                    id: youtubeVideoId,
                    image: mainImage,
                }}
                onClick={(index) => setActiveGalleryIndex(index)}
            />
            {activeGalleryIndex !== undefined && (
                <Gallery
                    galleryItems={galleryImages}
                    startIndex={activeGalleryIndex}
                    onCloseCallback={() => setActiveGalleryIndex(undefined)}
                />
            )}
        </div>
    );
};
